import { useState } from "react"
import Image from "next/image"
import { useLang } from "hooks/useLang"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const DiscoverWonderSection = ({ isLoading }: { isLoading: boolean }) => {
    const { t, lang } = useLang()
    const isAr = lang === "ar"

    const [email, setEmail] = useState("")
    const [emailError, setEmailError] = useState<string | null>(null)
    const [hasSubmitted, setHasSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)

    const validateEmail = (value: string) => {
        if (!EMAIL_REGEX.test(value)) return t("email_required")
        return null
    }

    const fakeSubmit = () => {
        setHasSubmitted(true)

        const error = validateEmail(email)
        if (error || isSubmitting) {
            setEmailError(error)
            return
        }

        setIsSubmitting(true)

        // 🧪 fake API request
        setTimeout(() => {
            setIsSubmitting(false)
            setIsSuccess(true)
            setEmail("")
            setEmailError(null)
            setHasSubmitted(false)
        }, 1200)
    }

    if (isLoading) return <></>

    return (
        <div className="w-full flex flex-col md:flex-row px-8 py-8 layout md:max-w-[1220px] h-auto md:h-[420px] items-center md:items-start">
            {/* Image */}
            <div className="flex flex-1 h-full overflow-hidden w-full">
                <Image
                    alt="diving"
                    src="/images/diving.jpg"
                    width={600}
                    height={420}
                    className={`flex flex-1 ${
                        isAr
                            ? "md:rounded-r-[15px] md:rounded-tr-[15px]"
                            : "md:rounded-l-[15px] md:rounded-tr-[0px]"
                    } rounded-t-[15px] h-auto`}
                />
            </div>

            {/* Content */}
            <div
                className={`flex flex-1 h-full justify-center ${
                    isAr
                        ? "md:rounded-bl-[15px] md:rounded-tl-[15px] md:rounded-b-[0px]"
                        : "md:rounded-r-[15px] md:rounded-bl-[0px]"
                } rounded-b-[15px] overflow-hidden bg-[#FFFFFFB2]`}
            >
                <div className="flex flex-col w-full h-full p-[40px] items-center">
                    <div className="w-full h-full flex flex-col justify-center gap-1 ">
                        <div className="md:w-[80%] w-full">
                            <Image
                                src="/svg/compass.svg"
                                alt="compass"
                                width={40}
                                height={40}
                                className="w-[40px] pb-2"
                            />
                            <span className="font-bold text-[#000] md:text-[30px] text-left ">
                                {t("discoverWonderSection_title")}
                            </span>
                        </div>

                        <p className="text-[#3E3E3E] text-sm">
                            {t("discoverWonderSection_subtitle")}
                        </p>

                        <div className="flex flex-col">
                            <div className="w-full flex flex-row gap-2 py-4 md:py-2">
                                {/* INPUT */}
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value)
                                        setIsSuccess(false)
                                    }}
                                    disabled={isSubmitting}
                                    placeholder={t("email_address")}
                                    className={`w-[70%] h-[45px] rounded-full px-4 text-[#000] border-[1px] bg-white
                    transition-all duration-300
                    ${
                        hasSubmitted && emailError
                            ? "border-red-400"
                            : "border-[#A0A0A0]"
                    }
                    ${isSubmitting ? "opacity-60" : "opacity-100"}`}
                                />

                                {/* BUTTON */}
                                <div
                                    onClick={
                                        !isSubmitting ? fakeSubmit : undefined
                                    }
                                    className={`bg-[#1FA3FF] rounded-full px-8 flex items-center justify-center cursor-pointer
                    transition-all duration-300
                    ${
                        isSubmitting
                            ? "animate-pulse scale-[0.98] cursor-not-allowed"
                            : ""
                    }`}
                                >
                                    <span className="text-white font-bold">
                                        {isSubmitting
                                            ? t("loading")
                                            : t("submit")}
                                    </span>
                                </div>
                            </div>

                            {/* ERROR MESSAGE (after submit only) */}
                            {hasSubmitted && emailError && (
                                <span className="text-red-500 text-[10px] px-2 mt-1">
                                    {emailError}
                                </span>
                            )}

                            {/* WARNING TEXT */}
                            <span className="text-[#3E3E3E] text-[10px] md:p-1 px-1 py-2">
                                {t("discoverWonderSectionWarrning")}
                            </span>

                            {/* SUCCESS MESSAGE */}
                            {isSuccess && (
                                <span className="text-[#1FA3FF] text-[11px] mt-1 animate-[fadeUp_0.4s_ease-out_forwards]">
                                    {t("email_received_success")}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DiscoverWonderSection
